import { useCallback, useEffect } from 'react'
import { Button } from '@/shared/components'
import { ENGLISH_TEST_MESSAGES } from '../constants/enrollmentContent'
import { StepHeader } from './StepHeader'
import { EnglishTestQuestionCard } from './englishProficiency/EnglishTestQuestionCard'
import type { EnglishTestModalProps } from './englishProficiency/types'
import { useEnglishTestModal } from './englishProficiency/useEnglishTestModal'

type EnglishTestPanelProps = EnglishTestModalProps & {
  currentStep: number
  totalSteps: number
  onBack: () => void
}

export function EnglishTestPanel({
  currentStep,
  totalSteps,
  onBack,
  isOpen,
  onClose,
  onComplete,
  assessmentId,
  defaultTest,
  initialAnswers = []
}: EnglishTestPanelProps) {
  const {
    answers,
    answeredCount,
    currentGroupIndex,
    groupStartIndex,
    handleAnswerSelect,
    handleCompleteTest,
    handleContinue,
    handleDateAnswerChange,
    handleTextAnswerBlur,
    handleTextAnswerChange,
    isLastGroup,
    isSubmitting,
    progressPercent,
    questions,
    questionsContainerRef,
    setCurrentGroupIndex,
    submissionError,
    totalGroups,
    totalQuestions,
    visibleQuestions
  } = useEnglishTestModal({
    assessmentId,
    defaultTest,
    initialAnswers,
    isOpen,
    onComplete
  })

  const scrollTestToTop = useCallback((behavior: ScrollBehavior = 'smooth') => {
    if (typeof window === 'undefined') {
      return
    }

    window.scrollTo({ top: 0, behavior })
  }, [])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    window.requestAnimationFrame(() => {
      scrollTestToTop('auto')
    })
  }, [isOpen, scrollTestToTop])

  if (!isOpen) {
    return null
  }

  const focusNextQuestion = (questionIndex: number) => {
    if (typeof window === 'undefined') {
      return
    }

    window.requestAnimationFrame(() => {
      const questionsContainer = questionsContainerRef.current

      if (!questionsContainer) {
        return
      }

      const nextQuestion = questionsContainer.querySelector<HTMLElement>(
        `[data-question-index="${questionIndex + 1}"]`
      )

      if (nextQuestion) {
        const topOffset = window.innerWidth < 640 ? 96 : 120
        const nextQuestionTop =
          nextQuestion.getBoundingClientRect().top + window.scrollY - topOffset

        window.scrollTo({
          top: Math.max(0, nextQuestionTop),
          behavior: 'smooth'
        })

        window.setTimeout(() => {
          nextQuestion.focus()
        }, 220)
        return
      }

      const advanceButton = document.querySelector<HTMLElement>('[data-assessment-action="advance"]')
      advanceButton?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      })

      window.setTimeout(() => {
        advanceButton?.focus()
      }, 220)
    })
  }

  return (
    <div className="bg-white px-4 py-2">
      <div className="mx-auto max-w-5xl animate-fade-in-up">
        <StepHeader currentStep={currentStep} totalSteps={totalSteps} onBack={onBack} />

        <div className="mb-4 flex items-start justify-center animate-fade-in-up sm:mb-5">
          <h1 className="modal-heading__title text-center">English Proficiency Test</h1>
        </div>

        <div className="overflow-hidden rounded-none border-0 bg-white shadow-none sm:rounded-[24px] sm:border sm:border-slate-200 sm:shadow-[0_16px_48px_rgba(15,23,42,0.08)]">
          <div className="border-b border-slate-200 bg-white px-0 py-3 sm:px-6 sm:py-4">
            <div>
              <div className="mb-2 flex items-center justify-between text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                <span>
                  Section {Math.min(currentGroupIndex + 1, totalGroups)} of {totalGroups}
                </span>
                <span>
                  {answeredCount} / {totalQuestions || defaultTest?.questionCount || 0} answered
                </span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-200">
                <div
                  className="h-full rounded-full bg-primary transition-all duration-300 ease-out"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
          </div>

          <div
            ref={questionsContainerRef}
            className="max-h-none overflow-visible overscroll-contain px-0 py-5 sm:px-6 sm:py-6"
          >
            {!assessmentId ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
                {ENGLISH_TEST_MESSAGES.assessmentMissingClose}
              </div>
            ) : !defaultTest ? (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
                {ENGLISH_TEST_MESSAGES.loadFailed}
              </div>
            ) : questions.length === 0 ? (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-800">
                {ENGLISH_TEST_MESSAGES.noQuestions}
              </div>
            ) : (
              <div className="space-y-4">
                {submissionError ? (
                  <div className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
                    {submissionError}
                  </div>
                ) : null}

                {visibleQuestions.map((question, visibleIndex) => {
                  const questionIndex = groupStartIndex + visibleIndex

                  return (
                    <EnglishTestQuestionCard
                      key={question.id ?? `${question.questionText}-${questionIndex}`}
                      question={question}
                      questionIndex={questionIndex}
                      selectedAnswer={answers[questionIndex]}
                      onAnswerSelect={(index, optionIndex) => {
                        handleAnswerSelect(index, optionIndex)
                        focusNextQuestion(index)
                      }}
                      onTextAnswerChange={handleTextAnswerChange}
                      onTextAnswerBlur={handleTextAnswerBlur}
                      onDateAnswerChange={handleDateAnswerChange}
                    />
                  )
                })}
              </div>
            )}
          </div>

          <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 px-0 py-4 backdrop-blur supports-[backdrop-filter]:bg-white/90 sm:px-6 sm:backdrop-blur-0">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-3 sm:flex-row">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  className="hover:-translate-y-0.5 active:scale-95"
                  disabled={isSubmitting}
                >
                  Cancel Test
                </Button>

                {currentGroupIndex > 0 ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCurrentGroupIndex(index => index - 1)
                      window.requestAnimationFrame(() => {
                        scrollTestToTop()
                      })
                    }}
                    className="hover:-translate-y-0.5 active:scale-95"
                    disabled={isSubmitting}
                  >
                    Previous
                  </Button>
                ) : null}
              </div>

              <Button
                type="button"
                variant="secondary"
                data-assessment-action="advance"
                onClick={() => {
                  if (isLastGroup) {
                    void handleCompleteTest()
                    return
                  }

                  void handleContinue().then(() => {
                    window.requestAnimationFrame(() => {
                      scrollTestToTop()
                    })
                  })
                }}
                disabled={questions.length === 0 || isSubmitting || !assessmentId || !defaultTest}
                loading={isSubmitting && isLastGroup}
                className="hover:-translate-y-0.5 active:scale-95"
              >
                {isLastGroup ? 'Complete Test' : 'Next Questions'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
